import { DestroyRef, Injectable, computed, effect, inject, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationCancel, NavigationEnd, NavigationError, Router, type ActivatedRouteSnapshot } from '@angular/router';
import { filter, map } from 'rxjs';

/**
 * "지금 활성화된 단체(org)가 무엇인가"를 앱 전역에 공유하는 signal.
 *
 * sbe-sms의 기존 `ActiveContextService`(`apps/sbe-sms/user-web/src/core/active-context.service.ts`)를
 * 이 lib으로 승격한 것이다 — sbe-commerce도 `SpinoffOrgContextButtonComponent.activeOrgId`를
 * 채울 방법이 없었다(세 셸 어디서도 값을 넘기지 않아 항상 `null`). 두 화면이 같은 문제를 다시
 * 각자 풀지 않도록 여기 하나로 통일한다(`plans/active/2026-08-09-shared-active-org-context.md`).
 *
 * **컨벤션 예외**: `docs/conventions/conventions-angular-practices.md`(§라우트 파라미터와
 * 이벤트)는 "서비스가 `Router.events`/`ActivatedRoute.paramMap`으로 파라미터를 스스로
 * 추출하면 안 된다 — 라우트 컴포넌트가 `input()`으로 받아 서비스에 push해야 한다"고 명시한다.
 * 이 서비스는 의도적으로 그 패턴을 따르지 않는다 — 인증 경계 강제 리셋(로그아웃 시 라우트
 * 트리 전체를 봐야 판단 가능), 쿼리 파라미터 폴백(풀 페이지 리다이렉트로 in-memory 상태가
 * 유실되는 경우)이 "라우트 하나·컴포넌트 하나가 자기 파라미터를 책임진다"는 컨벤션의 기본
 * 가정을 벗어나기 때문이다 — 자세한 근거는 위 플랜 문서 참고. `SpinoffOrgSwitcherComponent`는
 * 아래 `markPersonalSelected()`/`markOrgSelected()` 호출을 위해 이 서비스를 주입하지만
 * `Router`/`ActivatedRoute`를 직접 참조하진 않는다 — 라우터를 직접 아는 코드는 여전히 이
 * 서비스 하나뿐이다.
 *
 * **규칙은 단순하다: `:orgId`가 URL에 있으면 그 org, 없으면 개인(null)이다.** 예외 없음.
 *
 * 2026-08-14 이전엔 "org를 모르는 라우트(대시보드·단체 목록 등)를 거칠 때는 이전 컨텍스트를
 * 유지한다"는 sticky 정책과, 그 안에서 "이 라우트는 명시적 개인 모드 착지점"이라고 앱이
 * 표시하는 `data: { orgScopable: true }` 마커가 있었다. 이 마커를 빠뜨리면(실제로
 * sbe-schedule-web의 "/dashboard"에서 발생) `<spinoff-org-switcher>`에서 "개인"을 눌러도
 * 이전 단체가 그대로 활성 상태로 남는 조용한 버그가 생겼다. sms의 `credits` 라우트가 이미 같은
 * 이유로 이 sticky 예외를 버리고 org 버전(`orgs/:orgId/credits`)과 명시적 개인 버전으로
 * 갈라섰던 전례를 그대로 규칙화했다 — "URL이 org를 안 담고 있다 = 개인"을 예외 없는 기본값으로
 * 두면, 마커를 깜빡했을 때의 실패 모드가 "조용히 stale org가 새는 것"에서 "그냥 개인 모드로
 * 보이는 것"(눈에 띄고 훨씬 덜 위험함)으로 바뀐다. 페이지 자체가 org에 따라 내용이 달라진다면
 * `orgs/:orgId/그-경로`로 org 버전을 따로 두면 된다 — sms의 `credits`/`send`/`history`/
 * `settings`가 이 패턴이다. "단체 목록"처럼 org 선택 자체가 목적인 페이지도 예외를 두지
 * 않는다 — 그 페이지에 있다는 사실 자체가 이미 "지금은 특정 org 안에 있지 않다"는 뜻이다.
 *
 * 위 규칙은 인증 영역(`data: { appShell: true }`) 안에서만 적용된다. 로그아웃 후 '/'로
 * 이동하거나 세션 만료로 '/login'으로 리다이렉트되는 경우처럼 인증 영역 밖으로 나가는 라우트는
 * `appShell` 마커가 없으므로 항상 null이다 — 그렇지 않으면 로그아웃 후 같은 탭에서 다른
 * 사용자로 로그인했을 때 이전 사용자의 org 컨텍스트가 그대로 노출된다.
 *
 * `data: { orgQueryParam: true }` 라우트(sms의 `credits/result` 등 PG 결제 리다이렉트 복귀
 * 지점)는 브라우저 전체 페이지 이동이라 in-memory 상태(이 서비스의 signal)가 유실된다 — 그래서
 * route param이 아니라 쿼리 파라미터 `orgId`로 컨텍스트를 복원한다. `orgId` 쿼리가 없으면(개인
 * 결제 완료/실패) 명시적으로 null(개인 모드)이다.
 *
 * `orgId`를 라우트 스냅샷 체인의 leaf가 아니라 **각 노드 자신의 로컬 `paramMap`**에서
 * 찾는다(루트→leaf로 순회하며 가장 깊은 매치를 기억) — leaf의 병합된 `paramMap`을 쓰는
 * 방식은 `paramsInheritanceStrategy: 'always'`에 암묵적으로 의존하는데, 이 서비스를 쓰는
 * 모든 앱이 그 전역 라우터 설정을 켜야 할 이유는 없다. `appShell` 플래그도 같은 이유로 매
 * 노드를 직접 순회해 확인한다(Angular의 자동 data 상속에 기대지 않음).
 *
 * **`markPersonalSelected()`/`markOrgSelected()`**: 위 규칙이 예외 없이 URL만 보므로 정확성
 * 자체엔 이제 필요 없다 — `<spinoff-org-switcher>` 클릭 후 앱이 내비게이션을 마치면 라우트
 * 기반 값이 알아서 맞게 갱신된다. 다만 내비게이션은 비동기라(특히 지연 로드 청크) 클릭과
 * `NavigationEnd` 사이에 몇 프레임의 지연이 있을 수 있다 — 그 사이에도 스위처 체크 표시가
 * 즉시 따라오도록 클릭 시점에 미리 반영하는 순수 체감속도 최적화다. 실제로 `:orgId` 있는
 * 라우트에 도달하면(또는 `markOrgSelected()` 호출 시) 오버라이드를 해제해 라우트 기반 값으로
 * 되돌아간다.
 */
@Injectable({ providedIn: 'root' })
export class SpinoffActiveOrgContextService {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly explicitPersonal = signal(false);

  private readonly routeOrgId = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.resolveOrgId()),
    ),
    { initialValue: this.resolveOrgId() },
  );

  readonly orgId = computed(() => (this.explicitPersonal() ? null : this.routeOrgId()));

  constructor() {
    effect(() => {
      if (this.routeOrgId()) this.explicitPersonal.set(false);
    });

    // markPersonalSelected()의 낙관적 오버라이드는 이후 실제 내비게이션이 취소되거나(가드
    // 거부 등) 에러로 끝나면 되돌릴 방법이 없었다 — 그 경우 실제 라우트는 그대로 org
    // 스코프인데 orgId()는 계속 null(개인 모드)을 보고해 스위처 체크 표시가 실제 화면과
    // 어긋난 채로 남는다(code-review 지적). 내비게이션이 성립하지 않았다는 뜻이므로
    // routeOrgId()가 가리키는 실제(변화 없는) 라우트로 그냥 되돌리면 된다.
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationCancel || event instanceof NavigationError),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.explicitPersonal.set(false));
  }

  markPersonalSelected(): void {
    this.explicitPersonal.set(true);
  }

  markOrgSelected(): void {
    this.explicitPersonal.set(false);
  }

  private resolveOrgId(): string | null {
    let snapshot: ActivatedRouteSnapshot = this.router.routerState.snapshot.root;
    let insideAppShell = !!snapshot.data['appShell'];
    let deepestOrgId = snapshot.paramMap.get('orgId');
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
      insideAppShell ||= !!snapshot.data['appShell'];
      const localOrgId = snapshot.paramMap.get('orgId');
      if (localOrgId) deepestOrgId = localOrgId;
    }

    if (!insideAppShell) return null;
    if (deepestOrgId) return deepestOrgId;
    if (snapshot.data['orgQueryParam']) return snapshot.queryParamMap.get('orgId');
    return null;
  }
}
